import { z } from 'zod';

import { WorkflowStatusSchema } from './workflow-status';

// ---------------------------------------------------------------------------
// "My issues" — the signed-in user's issues across every GitHub repo their
// classic PAT can see AND every connected Linear workspace, grouped by their
// relationship to each issue. Served by GET /api/account/issues, rendered by
// the Issues view, and exposed as the `list_my_issues` MCP tool. Dates are ISO
// strings, as everywhere in contracts.
// ---------------------------------------------------------------------------

/** Open/closed only — issues have no draft or merged state (unlike PRs). */
export const IssueStateSchema = z.enum(['open', 'closed']);
export type IssueState = z.infer<typeof IssueStateSchema>;

/** The source an issue came from. */
export const IssueProviderSchema = z.enum(['github', 'linear']);
export type IssueProvider = z.infer<typeof IssueProviderSchema>;

/**
 * Linear's priority scale: 0 none, 1 urgent, 2 high, 3 normal, 4 low. GitHub
 * issues have no native priority, so GitHub rows carry `null`.
 */
export const IssuePrioritySchema = z.number().int().min(0).max(4);
export type IssuePriority = z.infer<typeof IssuePrioritySchema>;

/** A GitHub issue named by a native dependency or parent/sub-issue edge. */
export const GithubIssueRelationshipReferenceSchema = z.object({
  sourceId: z.string().min(1),
  displayKey: z.string().min(1),
  scope: z.string().min(1),
  title: z.string(),
  url: z.url(),
  state: IssueStateSchema,
  /** ISO close time when this related issue is closed; absent on older snapshots. */
  closedAt: z.iso.datetime().nullable().optional(),
  labels: z.array(z.string()),
  source: z.enum(['first_class', 'issue_body']).optional(),
  confidence: z.enum(['high', 'low']).optional(),
});

export const SOFT_ISSUE_RELATIONSHIP_KINDS = [
  'builds-on',
  'extends',
  'part-of',
  'feeds',
  'related',
] as const;
export type GithubIssueRelationshipReference = z.infer<
  typeof GithubIssueRelationshipReferenceSchema
>;

export const GithubMergedPullRequestReferenceSchema = z.object({
  number: z.number().int().positive(),
  title: z.string(),
  url: z.url(),
  mergedAt: z.iso.datetime(),
  kind: z.enum(['closing', 'part-of', 'reference', 'linked-branch']),
});
export type GithubMergedPullRequestReference = z.infer<
  typeof GithubMergedPullRequestReferenceSchema
>;

/**
 * Linear's own issue-relation types, normalised to a direction. Linear records a
 * relation once and exposes it from both ends (`relations` / `inverseRelations`),
 * so the inverse side is named here rather than left for each consumer to invert.
 * `similar` collapses into `related`: Linear draws no actionable distinction and
 * neither surface treats them differently.
 */
export const LINEAR_ISSUE_RELATION_KINDS = [
  'duplicate-of',
  'duplicated-by',
  'blocks',
  'blocked-by',
  'related',
] as const;
export const LinearIssueRelationKindSchema = z.enum(
  LINEAR_ISSUE_RELATION_KINDS,
);
export type LinearIssueRelationKind = z.infer<
  typeof LinearIssueRelationKindSchema
>;

/**
 * One Linear-asserted relation from an issue to another issue. Linear-only, the
 * mirror image of `relationships` being GitHub-only: a provider fact, fetched
 * best-effort, so its absence means "not loaded" as often as "none exist".
 */
export const LinearIssueRelationSchema = z.object({
  kind: LinearIssueRelationKindSchema,
  /** The other issue's human reference, e.g. `ENG-46`. */
  key: z.string().min(1),
  /** The other issue's `Workspace/TEAM` scope. */
  scope: z.string().min(1),
  url: z.url(),
  state: IssueStateSchema,
});
export type LinearIssueRelation = z.infer<typeof LinearIssueRelationSchema>;

/** Native GitHub dependency and parent/sub-issue facts for one issue. */
export const GithubIssueRelationshipsSchema = z.object({
  /** Exact number of currently-open blockers. */
  openBlockers: z.number().int().nonnegative(),
  /** Bounded blocker references, used to explain the blocked marker. */
  blockedBy: z.array(GithubIssueRelationshipReferenceSchema),
  /** True when `blockedBy` was capped before every blocker reference was read. */
  blockedByTruncated: z.boolean(),
  /** Most recently closed direct blocker, when its timestamp was loaded. */
  latestClosedBlocker:
    GithubIssueRelationshipReferenceSchema.nullable().optional(),
  /** Exact number of open issues this issue currently unblocks. */
  unblocks: z.number().int().nonnegative(),
  parent: GithubIssueRelationshipReferenceSchema.nullable(),
  /** Exact number of unfinished direct sub-issues. */
  openSubIssues: z.number().int().nonnegative(),
  totalSubIssues: z.number().int().nonnegative(),
  subIssues: z.array(GithubIssueRelationshipReferenceSchema).optional(),
  mergedPullRequests: z
    .array(GithubMergedPullRequestReferenceSchema)
    .optional(),
  mergedPullRequestsTruncated: z.boolean().optional(),
  /** Latest non-bot issue edit/comment/lifecycle change, for landed-evidence invalidation. */
  latestSubstantiveActivityAt: z.iso.datetime().nullable().optional(),
  /**
   * Same-repo issue keys this open issue explicitly claims to deliver. Derived
   * from bounded body/cross-reference evidence for the live next-work ranking;
   * absent on persisted snapshots and when no qualifying claim exists.
   */
  delivers: z.array(z.string()).optional(),
  /** Bounded repo-relative paths/globs declared in the issue body's Scope section. */
  declaredScope: z.array(z.string()).optional(),
  /** First bounded step from a Dogfood first / Before starting / Prerequisite convention. */
  prerequisiteStep: z.string().min(1).max(160).optional(),
  /** Bounded explanation when issue-body language marks this as a parked record. */
  parked: z.string().min(1).max(160).optional(),
  /**
   * Bounded explanation when the body structurally declares a living tracker —
   * a ledger whose own checklist is unblocked while the implementation it
   * tracks waits elsewhere. Distinct from `parked`: a parked record is not yet
   * actionable at all, whereas a tracker is never the actionable unit.
   */
  tracker: z.string().min(1).max(160).optional(),
  /** Explicit, bounded prose relationships retained separately from hard blockers. */
  declared: z
    .array(
      z.object({
        kind: z.enum([
          'blocked-by',
          'depends-on',
          'coordinates-with',
          'follow-up-to',
          'unblocks',
          'builds-on',
          'extends',
          'part-of',
          'feeds',
          'related',
        ]),
        target: z.string(),
        source: z.literal('issue_body'),
        confidence: z.literal('low'),
      }),
    )
    .optional(),
  /** Most recently closed direct child, when its timestamp was loaded. */
  latestClosedSubIssue:
    GithubIssueRelationshipReferenceSchema.nullable().optional(),
});
export type GithubIssueRelationships = z.infer<
  typeof GithubIssueRelationshipsSchema
>;

/** A single issue row, normalised across GitHub and Linear. */
export const IssueSummarySchema = z.object({
  provider: IssueProviderSchema,
  /**
   * Globally-unique, stable row id. GitHub: `github:owner/repo#123`. Linear:
   * `linear:<workspaceId>:ENG-45`. Used as the React key and for cross-provider
   * dedupe.
   */
  sourceId: z.string().min(1),
  /** Human reference: `#123` (GitHub) or `ENG-45` (Linear). */
  displayKey: z.string().min(1),
  /** Grouping scope: `owner/repo` (GitHub) or `Workspace/TEAM` (Linear). */
  scope: z.string().min(1),
  title: z.string(),
  url: z.url(),
  state: IssueStateSchema,
  /** Normalised pipeline stage (derived); distinct from `state`. */
  workflowStatus: WorkflowStatusSchema,
  /** ISO — when it entered `workflowStatus` (best-effort until the event log). */
  workflowStatusSince: z.iso.datetime(),
  /** GitHub login or Linear creator display name; "unknown" when omitted. */
  author: z.string(),
  /**
   * True when the provider reports the author as automation — a GraphQL `Bot`
   * actor or a `[bot]`-suffixed login. Absent means "not established" (Linear,
   * and snapshots written before this shipped), never "proven human".
   */
  authorIsBot: z.boolean().optional(),
  /** True when GitHub reports the issue as pinned to its repository. */
  pinned: z.boolean().optional(),
  /** Assignee logins (GitHub) / display names (Linear); empty when unassigned. */
  assignees: z.array(z.string()),
  /** Linear priority (0–4); `null` for GitHub (no native priority). */
  priority: IssuePrioritySchema.nullable(),
  /** Conversation comment count (GitHub/Linear `comments.totalCount`); `0` when omitted. */
  comments: z.number().int().nonnegative(),
  /**
   * True when `comments` is a capped lower bound rather than an exact total —
   * Linear exposes no count primitive, so a very chatty issue reports the cap and
   * sets this so the UI can show "N+". Absent/`false` for exact counts (GitHub).
   */
  commentsTruncated: z.boolean().optional(),
  /** GitHub labels; absent for Linear and snapshots fetched before native relationships. */
  labels: z.array(z.string()).optional(),
  /** GitHub-only native dependency and parent/sub-issue facts. */
  relationships: GithubIssueRelationshipsSchema.optional(),
  /**
   * Linear-only issue relations, capped upstream. Absent for GitHub, and absent
   * for Linear when the best-effort relation read did not run or failed — never
   * read an absent list as "this issue has no relations".
   */
  relations: z.array(LinearIssueRelationSchema).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type IssueSummary = z.infer<typeof IssueSummarySchema>;

/**
 * One bucket of results plus how many actually matched, so the UI can say
 * "showing 50 of 183". Linear has no total-count primitive, so a Linear-only
 * bucket reports `totalCount === items.length` with `truncated` driven by the
 * provider's `hasNextPage`.
 */
export const IssueBucketSchema = z.object({
  items: z.array(IssueSummarySchema),
  totalCount: z.number().int().nonnegative(),
  truncated: z.boolean(),
});
export type IssueBucket = z.infer<typeof IssueBucketSchema>;

/** The user's relationship to an issue. Each is its own per-provider query. */
export const IssueBucketKeySchema = z.enum([
  'created',
  'assigned',
  'mentioned',
]);
export type IssueBucketKey = z.infer<typeof IssueBucketKeySchema>;
/** The three bucket keys, in display order. */
export const IssueBucketKeys = IssueBucketKeySchema.options;

export const IssuesSchema = z.object({
  created: IssueBucketSchema,
  assigned: IssueBucketSchema,
  mentioned: IssueBucketSchema,
});
export type Issues = z.infer<typeof IssuesSchema>;

/** Per-provider connection state so the UI can prompt connecting each provider. */
export const IssueProviderConnectionSchema = z.object({
  provider: IssueProviderSchema,
  connected: z.boolean(),
  /** In-scope connected account labels (Linear workspace names; GitHub login). */
  accounts: z.array(z.string()),
});
export type IssueProviderConnection = z.infer<
  typeof IssueProviderConnectionSchema
>;

/** Response envelope for `GET /api/account/issues`. */
export const IssuesResponseSchema = z.object({
  /** True when at least one provider is connected and in scope. */
  connected: z.boolean(),
  /** Per-provider connection state (github, then linear). */
  providers: z.array(IssueProviderConnectionSchema),
  buckets: IssuesSchema,
  /** True when one or more provider buckets failed to load. */
  partial: z.boolean(),
  /**
   * Provider-qualified bucket keys that failed, e.g. `github:assigned` or
   * `linear:created` (a merged bucket can fail for one provider only). Empty
   * unless `partial`.
   */
  failedBuckets: z.array(z.string()),
});
export type IssuesResponse = z.infer<typeof IssuesResponseSchema>;

function emptyBucket(): IssueBucket {
  return { items: [], totalCount: 0, truncated: false };
}

/** All three buckets empty — used for the not-connected and load-failure paths. */
export function emptyIssues(): Issues {
  return {
    created: emptyBucket(),
    assigned: emptyBucket(),
    mentioned: emptyBucket(),
  };
}
